import { useNavigate } from 'react-router-dom';
import type { VenueDto } from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core/i18n';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerTitle } from '@/components/ui/drawer';

interface VenueClosedSheetProps {
  /** The closed venue being explained; `null` keeps the sheet closed. */
  venue: VenueDto | null;
  onClose: () => void;
}

/**
 * Explains why a venue can't be booked and links to its proposed alternatives. Render it inside the
 * Where drawer so it stacks as a nested drawer on top of the venue list.
 */
export function VenueClosedSheet({ venue, onClose }: VenueClosedSheetProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const proposals = venue?.venueProposals?.filter(p => !!p.venueNodeKey) ?? [];

  return (
    <Drawer
      open={!!venue}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent
        className="bg-jncquoi-neutral-1000 after:bg-jncquoi-neutral-1000 pt-4"
        overlayClassName="bg-jncquoi-neutral-1000/70"
      >
        <div className="flex flex-col gap-4 px-4 pt-6 pb-10">
          {venue?.venueClosedTitle && (
            <DrawerTitle className="text-jncquoi-neutral-00 text-[24px]">
              {venue.venueClosedTitle}
            </DrawerTitle>
          )}
          {venue?.venueClosedDescription && (
            <DrawerDescription className="text-jncquoi-neutral-00 text-[16px]">
              {venue.venueClosedDescription}
            </DrawerDescription>
          )}
        </div>

        <DrawerFooter className="border-jncquoi-neutral-900 gap-1">
          {proposals.map(proposal => (
            <Button
              key={proposal.venueNodeKey}
              variant="primary-solid"
              palette="white"
              size="md"
              className="w-full gap-2"
              onClick={() => {
                onClose();
                navigate(`/venue-detail/${proposal.venueNodeKey}`);
              }}
            >
              <span className="truncate">{proposal.name}</span>
              {proposal.location && <span className="truncate opacity-60">{proposal.location}</span>}
            </Button>
          ))}
          <Button variant="tertiary-link" palette="white" size="sm" className="w-full" onClick={onClose}>
            {t('App.Labels.Cancel')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
